import React, { useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";

import moment from "moment";
import { Formik } from "formik";
import { postNotification } from "../../../components/utilities/Notifications";
import {
  getArmyPickerArmies,
  handlePaging
} from "../../../components/api/APIUtilities";

import TextFieldSelect from "../../../components/utilities/TextFieldSelect";
import ListResponsive from "../../../components/other/ListResponsive";
import StyledMenu from "../../../components/layout/StyledMenu";

import ArmyListCardV2 from "./ArmyListCardV2";
import MUITags from "../../../components/utilities/MUITags";
import DatePickerComponent from "../../../components/utilities/DatePickerComponent";
import GenericPicker from "../../../components/utilities/GenericPicker";
import {
  BCPAngleDown,
  BCPAngleRight,
  BCPAnglesDown,
  BCPAnglesUp
} from "../../../components/icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../../global/environmentVariables";

import ModalLoadingMessage from "../../../components/modals/ModalLoadingMessage";
import { getDetachments } from "./FactionUtilities";
import { useTranslation } from "react-i18next";

const SINGLE_GAME_SYSTEM = getEnvironmentVariable("SINGLE_GAME_SYSTEM");

const searchLimit = 100;
const initialValues = {
  eventName: "",
  startDate: new Date(moment().subtract(1, "month").startOf("month")),
  endDate: new Date(moment().endOf("month")),
  numberOfRounds: "",
  numberOfPlayers: "",

  factionId: "",
  armyId: "",
  detachment: "",

  placing: "",
  losses: "",
  gamesPlayed: "",

  keywords: [],
  keywordsList: "",

  excludeKeywords: [],
  excludeKeywordsList: "",

  sortKey: "eventDate",
  sortAscending: false
};

export default function ArmyListSearchWidgetV2(props) {
  const { gameSystem, heading, containerSX, dense, imageCache } = props;
  const { t } = useTranslation("translation", {
    keyPrefix: "components.ArmyListSearchWidgetV2"
  });

  const sortOptions = {
    _created_at: t("sortOptions.createdDate"),
    eventDate: t("sortOptions.eventDate"),
    placing: t("sortOptions.placing"),
    wins: t("sortOptions.numberOfWins"),
    losses: t("sortOptions.numberOfLosses"),
    numberOfRounds: t("sortOptions.numberOfRounds")
  };

  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  // query states
  const [searchValues, setSearchValues] = useState(initialValues);
  const [apiFilters, setApiFilters] = useState(null);
  const [nextKey, setNextKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [advancedSearch, setAdvancedSearch] = useState(false);
  const [sortAnchor, setSortAnchor] = useState(null);

  const [factions, setFactions] = useState(null);
  const [faction, setFaction] = useState(null);
  const [army, setArmy] = useState(null);
  const [detachments, setDetachments] = useState(null);

  // view states
  const [lists, setLists] = useState(null);

  const search = async (_apiFilters, _reset) => {
    try {
      let _newApiFilters = _apiFilters || apiFilters;
      var _lists = await handlePaging(
        "/armylists",
        _newApiFilters,
        _reset ? null : nextKey,
        setNextKey,
        "v2"
      );
      setSearching(false);
      setLists(lists?.length && !_reset ? [...lists, ..._lists] : _lists);
      if (_lists.length !== _newApiFilters.limit) {
        setHasMore(false);
        if (!_lists?.length) {
          postNotification("info", t("noListsFound"));
        }
      }
      setAdvancedSearch(false);
    } catch (error) {
      postNotification("error", error);
      setSearching(false);
      setLists(null);
      setNextKey(null);
      setHasMore(true);
    }
  };

  useEffect(() => {
    const loadPage = async () => {
      const { armyArray, subFactionDict } = await getArmyPickerArmies(
        gameSystem?.id || SINGLE_GAME_SYSTEM,
        {}
      );

      setFactions(armyArray);
      setLoading(false);
    };
    setLoading(true);
    loadPage();
    // eslint-disable-next-line
  }, []);

  return (
    <Grid container sx={{ gap: 2, ...containerSX }}>
      {heading && (
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            display: "flex"
          }}
        >
          <Grid item sx={{ flex: 1 }}>
            <Typography variant="h3">{heading}</Typography>
          </Grid>
        </Grid>
      )}
      <Grid
        item
        xs={12}
        sx={{
          p: 2
        }}
      >
        <Formik
          initialValues={searchValues}
          enableReinitialize
          onSubmit={async (values) => {
            try {
              setLists(null);
              setNextKey(null);
              setHasMore(true);
              let query = {
                limit: searchLimit,
                sortKey: values.sortKey,
                sortAscending: values.sortAscending,

                gameSystemId: gameSystem?.id || SINGLE_GAME_SYSTEM
              };
              if (values.eventName) query.eventName = values.eventName;
              if (values?.startDate && values?.startDate !== "") {
                query.startDate = moment(values.startDate, "YYYY-MM-DD").format(
                  "YYYY-MM-DD"
                );
              }
              if (values?.endDate && values?.endDate !== "") {
                query.endDate = moment(values.endDate, "YYYY-MM-DD").format(
                  "YYYY-MM-DD"
                );
              }
              if (
                query?.startDate &&
                query?.endDate &&
                moment(query.startDate).isAfter(moment(query.endDate))
              ) {
                throw new Error(t("startDateBeforeEndDate"));
              }
              if (values.numberOfRounds !== "")
                query.numberOfRounds = parseInt(values.numberOfRounds);

              if (values.factionId) query.factionId = values.factionId;
              if (values.armyId) query.armyId = values.armyId;
              if (values.detachment) query.detachment = values.detachment;
              if (values.keywords?.length)
                query["keywords[]"] = values.keywords;
              if (values.excludeKeywords?.length)
                query["excludeKeywords[]"] = values.excludeKeywords;

              if (values.losses !== "") query.losses = parseInt(values.losses);

              if (values.placing !== "")
                query.placing = parseInt(values.placing);

              if (values.gamesPlayed !== "")
                query.gamesPlayed = parseInt(values.gamesPlayed);

              setSearching(true);
              setApiFilters(query);
              search(query, true);
            } catch (error) {
              postNotification("error", error);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values,
            setFieldValue,
            setFieldTouched,
            handleReset
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {gameSystem?.hasFactions && (
                  <>
                    <Grid item xs={12}>
                      <TextFieldSelect
                        list={
                          !factions?.length
                            ? []
                            : factions.map((_faction) => {
                                return {
                                  value: _faction.id,
                                  label: _faction.name
                                };
                              })
                        }
                        name={"factionId"}
                        label={t("faction")}
                        value={values.factionId}
                        fullWidth
                        variant="outlined"
                        margin="none"
                        onChange={(e) => {
                          let _faction = factions.filter(
                            (el) => el.id === e.target.value
                          )?.[0];
                          setFaction(_faction);
                          let _detachments = getDetachments(
                            gameSystem.code,
                            _faction
                          );
                          setDetachments(_detachments);
                        }}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        error={Boolean(touched.factionId && errors.factionId)}
                        helperText={touched.factionId && errors.factionId}
                        clearAll
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextFieldSelect
                        list={
                          !faction?.childArmies?.length
                            ? []
                            : faction.childArmies.map((childArmy) => {
                                return {
                                  value: childArmy.id,
                                  label: childArmy.name
                                };
                              })
                        }
                        name={"armyId"}
                        label={t("army")}
                        value={values.armyId}
                        fullWidth
                        variant="outlined"
                        margin="none"
                        onChange={(e) => {
                          let _army = faction?.childArmies.filter(
                            (el) => el.id === e.target.value
                          )?.[0];
                          setArmy(_army);
                          let _detachments = getDetachments(
                            gameSystem.code,
                            faction,
                            _army
                          );
                          setDetachments(_detachments);
                        }}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        error={Boolean(touched.armyId && errors.armyId)}
                        helperText={touched.armyId && errors.armyId}
                        clearAll
                        disabled={!faction?.childArmies?.length}
                      />
                    </Grid>

                    {/* {gameSystem?.code === 1 || gameSystem?.code === 4 ? (
                      <Grid item xs={12}>
                        <TextFieldSelect
                          list={
                            !detachments?.length
                              ? []
                              : detachments.map(
                                  (detachment) => detachment.detachment
                                )
                          }
                          name="detachment"
                          label={
                            gameSystem?.code === 4
                              ? "Battle Formation"
                              : "Detachment"
                          }
                          value={values.detachment}
                          fullWidth
                          variant="outlined"
                          margin="none"
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          error={Boolean(
                            touched.detachment && errors.detachment
                          )}
                          helperText={touched.detachment && errors.detachment}
                          clearAll
                          disabled={!detachments?.length}
                        />
                      </Grid>
                    ) : (
                      <></>
                    )} */}

                    <Grid item xs={12} sm={6}>
                      <DatePickerComponent
                        name="startDate"
                        label={t("startDate")}
                        variant="outlined"
                        margin="none"
                        value={values.startDate}
                        setFieldValue={setFieldValue}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePickerComponent
                        name="endDate"
                        label={t("endDate")}
                        variant="outlined"
                        margin="none"
                        value={values.endDate}
                        setFieldValue={setFieldValue}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      onClick={() => {
                        setAdvancedSearch(!advancedSearch);
                      }}
                      sx={{
                        "&:hover": {
                          cursor: "pointer"
                        }
                      }}
                    >
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap"
                        }}
                      >
                        {advancedSearch ? <BCPAngleDown /> : <BCPAngleRight />}
                        <span>{t("advancedSearch")}</span>
                      </Typography>
                    </Grid>
                    {advancedSearch && (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            name="eventName"
                            label={t("eventName")}
                            value={values.eventName}
                            variant="outlined"
                            fullWidth
                            margin="none"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.eventName && errors.eventName
                            )}
                            helperText={touched.eventName && errors.eventName}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="numberOfRounds"
                            label={t("minimumRounds")}
                            value={values.numberOfRounds}
                            variant="outlined"
                            fullWidth
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.numberOfRounds && errors.numberOfRounds
                            )}
                            helperText={
                              touched.numberOfRounds && errors.numberOfRounds
                            }
                            type="number"
                            inputProps={{
                              min: 0
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <GenericPicker
                            name="placing"
                            label={t("placing")}
                            value={values.placing}
                            handleChange={handleChange}
                            list={[
                              { label: t("firstPlace"), value: 1 },
                              { label: t("topThree"), value: 3 },
                              { label: t("topEight"), value: 8 },
                              { label: t("topSixteen"), value: 16 }
                            ]}
                            error={Boolean(touched.placing && errors.placing)}
                            helperText={touched.placing && errors.placing}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <GenericPicker
                            name="losses"
                            label={t("winRate")}
                            value={values.losses}
                            handleChange={handleChange}
                            list={[
                              { label: t("undefeated"), value: 0 },
                              { label: t("oneOrFewerLosses"), value: 1 },
                              { label: t("twoOrFewerLosses"), value: 2 }
                            ]}
                            error={Boolean(touched.losses && errors.losses)}
                            helperText={touched.losses && errors.losses}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            name="gamesPlayed"
                            label={t("gamesPlayed")}
                            value={values.gamesPlayed}
                            variant="outlined"
                            fullWidth
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(
                              touched.gamesPlayed && errors.gamesPlayed
                            )}
                            helperText={
                              touched.gamesPlayed && errors.gamesPlayed
                            }
                            type="number"
                            inputProps={{
                              min: 0
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <MUITags
                            label={t("keywords")}
                            value={values.keywordsList}
                            name="keywordsList"
                            valueArray={values.keywords}
                            nameArray="keywords"
                            setFieldValue={setFieldValue}
                            handleBlur={handleBlur}
                            handleChange={handleChange}
                            error={Boolean(
                              touched.keywordsList && errors.keywordsList
                            )}
                            helperText={
                              touched.keywordsList && errors.keywordsList
                            }
                            fullWidth
                            variant="outlined"
                            margin="none"
                          />
                          <FormHelperText>
                            {t("keywordsHelperText")}
                          </FormHelperText>
                        </Grid>
                        <Grid item xs={12}>
                          <MUITags
                            label={t("excludeKeywords")}
                            value={values.excludeKeywordsList}
                            name="excludeKeywordsList"
                            valueArray={values.excludeKeywords}
                            nameArray="excludeKeywords"
                            setFieldValue={setFieldValue}
                            handleBlur={handleBlur}
                            handleChange={handleChange}
                            error={Boolean(
                              touched.excludeKeywordsList && errors.keywordsList
                            )}
                            helperText={
                              touched.excludeKeywordsList &&
                              errors.excludeKeywordsList
                            }
                            fullWidth
                            variant="outlined"
                            margin="none"
                          />
                          <FormHelperText>
                            {t("excludeKeywordsHelperText")}
                          </FormHelperText>
                        </Grid>

                        {/* {(gameSystem?.hasSubFactions ||
                      searchParams?.subFactionId) && (
                      <Grid item xs={12}>
                        <GenericPicker
                          name="subFactionId"
                          label={
                            gameSystem?.subFactionLabel ||
                            t("subFaction", {
                              keyPrefix: "misc.pairings"
                            })
                          }
                          value={values.subFactionId}
                          handleChange={handleChange}
                          inclAny={true}
                          list={armyDict?.[values.armyId] || []}
                          error={Boolean(
                            touched.subFactionId && errors.subFactionId
                          )}
                          helperText={
                            touched.subFactionId && errors.subFactionId
                          }
                          disabled={!values.armyId || values.armyId === ""}
                          clearable
                        />
                      </Grid>
                    )} */}
                      </>
                    )}
                  </>
                )}

                <Grid item xs={12} md={4}>
                  <ButtonGroup variant="outlined" fullWidth>
                    <Button
                      sx={{ maxWidth: "60px" }}
                      onClick={() => {
                        setFieldValue("sortAscending", !values.sortAscending);
                      }}
                    >
                      {values.sortAscending ? (
                        <BCPAnglesUp />
                      ) : (
                        <BCPAnglesDown />
                      )}
                    </Button>

                    <Button
                      onClick={(e) => {
                        setSortAnchor(e.currentTarget);
                      }}
                    >
                      {t("sortBy")} {sortOptions[values.sortKey]}
                    </Button>
                  </ButtonGroup>
                  <SortMenu
                    setFieldValue={setFieldValue}
                    sortKey={values.sortKey}
                    anchorEl={sortAnchor}
                    setAnchorEl={setSortAnchor}
                    sortOptions={sortOptions}
                    t={t}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    onClick={(e) => {
                      setLists(null);
                      setNextKey(null);
                      setSearchValues(initialValues);
                      setAdvancedSearch(false);
                      handleReset(e);
                    }}
                  >
                    {t("reset")}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    type="submit"
                  >
                    {t("search")}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Grid>
      {loading || searching ? (
        <ModalLoadingMessage
          message={
            loading
              ? t("loadingFactions", {
                  factionLabel: gameSystem?.factionLabel || t("faction")
                })
              : searching
                ? t("searchingForLists")
                : ""
          }
          open
        />
      ) : (
        <></>
      )}

      {lists?.length > 0 ? (
        <Grid item xs={12}>
          <ListResponsive
            originalList={lists}
            loadMore={search}
            hasMore={hasMore}
            scrollToTop
            rowProps={{
              mapFunction: (row, index) => (
                <ArmyListCardV2
                  key={index}
                  armyList={row}
                  dense={dense}
                  imageCache={imageCache}
                />
              )
            }}
          />
        </Grid>
      ) : (
        <></>
      )}
    </Grid>
  );
}

function SortMenu(props) {
  const { setFieldValue, sortKey, anchorEl, setAnchorEl, sortOptions, t } =
    props;
  const open = Boolean(anchorEl);

  return (
    <StyledMenu
      id="sort-options-menu"
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
    >
      {Object.keys(sortOptions).map((sortOption) => {
        return (
          <MenuItem
            key={sortOption}
            selected={sortOption === sortKey}
            onClick={(e) => {
              setFieldValue("sortKey", sortOption);
              setAnchorEl(null);
            }}
            disableRipple
          >
            {t("sortBy")} {sortOptions[sortOption]}
          </MenuItem>
        );
      })}
    </StyledMenu>
  );
}
